import { Injectable } from "@angular/core";
import { EdpEMacroFinishedModalComponent } from "@edp-components/edp-e-macro-finished-modal/edp-e-macro-finished-modal.component";
import { EdpERaceChallengeCompleteModalComponent } from "@edp-components/edp-e-race-challenge-complete-modal/edp-e-race-challenge-complete-modal.component";
import { EdpModalService } from "@edp-components/edp-modal";
import { IModalData } from "@edp-components/edp-modal/edp-modal.models";
import { BenefitsFactory, Card, Section } from "@edp-models/benefits.model";
import { UserParticipation } from "@edp-models/edp-campaigns.model";
import { MgmCode } from "@edp-models/edpe-mgm";
import { IUserEfficiency } from "@edp-models/energy-efficiency.model";
import { EfficiencyFactory } from "@edp-pages/edpe/dashboard-e/dashboard-e.model";
import { EdpeService } from "@edp-services/edpe.service";
import {
    EEdpeActions,
    SetDashboardSections,
    SetLoadedDashboard,
    SetMacroChallengeSubscription,
    SetQuizzs,
    SetUserEfficiency,
    ShowMacroChallengeModal,
} from "@edp-store/actions/edpe.actions";
import { ELayoutActions, ShowLoader } from "@edp-store/actions/layout.actions";
import { IAppState } from "@edp-store/state/app.state";
import { NgbModalOptions } from "@ng-bootstrap/ng-bootstrap";
import { Actions, createEffect, Effect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { EMPTY, of } from "rxjs";
import { catchError, exhaustMap, mergeMap, switchMap, tap } from "rxjs/operators";

@Injectable()
export class EdpeEffects {
    loadGetUserEfficiency$ = createEffect(() =>
        this.actions$.pipe(
            ofType(EEdpeActions.GetUserEfficiency),
            tap(() => this.store.dispatch(new ShowLoader(true))),
            switchMap(() => this.edpeService.getUserEfficiency().pipe(catchError(() => EMPTY))),
            switchMap((userEfficiency: IUserEfficiency) => {
                userEfficiency.efficiency = EfficiencyFactory.create(userEfficiency.efficiency);
                return of(new SetUserEfficiency(userEfficiency), new ShowLoader(false));
            })
        )
    );

    @Effect()
    refreshCards$ = this.actions$.pipe(
        ofType(EEdpeActions.GetDashboardSections),
        switchMap(() => this.edpeService.getDashboard()),
        mergeMap((dashboardSections: Section[]) => {
            dashboardSections.forEach(section => {
                const cardList: Card[] = [];
                section.cards.forEach(card => {
                    const cardItem: Card = BenefitsFactory.create(card);
                    cardItem.location = "dashboard";
                    cardItem.inMacro = section.challenge !== null;
                    cardList.push(cardItem);
                });
                section.cards = cardList;

                if (section.challenge && section.challenge.completed === 1 && section.challenge.modalShown === 0) {
                    this.store.dispatch(new ShowMacroChallengeModal(section));
                }
            });

            return of(new SetDashboardSections(dashboardSections), new SetLoadedDashboard(true));
        })
    );

    @Effect({ dispatch: false })
    showMacroChallengeModal$ = this.actions$.pipe(
        ofType(EEdpeActions.ShowMacroChallengeModal),
        tap((payload: ShowMacroChallengeModal) => {
            const isRaceOrFestival = payload.section.challenge.type === "RACE" || payload.section.challenge.type === "FESTIVAL";
            const options: NgbModalOptions = {
                windowClass:
                    !isRaceOrFestival
                        ? "modal-mobile modal-macro-finished"
                        : "edp-modal-window edp-run-challenge-modal",
                keyboard: true,
            };
            const modalData: IModalData = {
                showClose: true,
                collection: !!isRaceOrFestival
                ? payload.section.challenge : payload.section,
            };

            if (!!isRaceOrFestival) {
                this.edpModalService.custom(EdpERaceChallengeCompleteModalComponent, options, modalData);
                return;
            }
            this.edpModalService.removeAll();
            this.edpModalService.custom(EdpEMacroFinishedModalComponent, options, modalData);
        })
    );

    @Effect()
    setMacroChallengeSubscription$ = this.actions$.pipe(
        ofType(EEdpeActions.SetMacroChallengeSubscription),
        switchMap((payload: SetMacroChallengeSubscription) =>
            this.edpeService.setMacroChallengeSubscription(payload.payload).pipe(
                switchMap(() => EMPTY),
                catchError((error: Error) => EMPTY)
            )
        )
    );

    @Effect()
    getUserAvailableParishList$ = this.actions$.pipe(
        ofType(EEdpeActions.GetUserAvailableParishList),
        exhaustMap(() => {
            this.store.dispatch(new ShowLoader(true));
            return this.edpeService.getUserAvailableParishList().pipe(
                switchMap((userAvailableParishList: any) => [
                    { type: ELayoutActions.ShowLoader, payload: false },
                    { type: EEdpeActions.SetUserAvailableParishList, payload: userAvailableParishList },
                ]),
                catchError(() => [{ type: ELayoutActions.ShowLoader, payload: false }])
            );
        })
    );

    @Effect()
    getUserParticipation$ = this.actions$.pipe(
        ofType(EEdpeActions.GetUserParticipation),
        exhaustMap(() => {
            this.store.dispatch(new ShowLoader(true));
            return this.edpeService.getUserParticipation().pipe(
                switchMap((userParticipation: UserParticipation) => [
                    { type: ELayoutActions.ShowLoader, payload: false },
                    { type: EEdpeActions.SetUserParticipation, payload: userParticipation },
                ]),
                catchError(() => [{ type: ELayoutActions.ShowLoader, payload: false }])
            );
        })
    );

    @Effect()
    insertUserParticipation$ = this.actions$.pipe(
        ofType(EEdpeActions.InsertUserParticipation),
        switchMap((action: any) =>
            this.edpeService.insertUserParticipation(action.payload).pipe(
                switchMap(() => [
                    { type: ELayoutActions.ShowLoader, payload: false },
                    { type: EEdpeActions.GetUserParticipation, payload: false },
                ]),
                catchError(() => [{ type: ELayoutActions.ShowLoader, payload: false }])
            )
        )
    );

    @Effect()
    shareToEarn$ = this.actions$.pipe(
        ofType(EEdpeActions.ShareToEarn),
        switchMap((action: any) =>
            this.edpeService.shareToEarn(action.payload).pipe(
                switchMap(() => [
                    { type: ELayoutActions.ShowLoader, payload: false },
                    { type: EEdpeActions.GetUserParticipation, payload: false },
                ]),
                catchError(() => [{ type: ELayoutActions.ShowLoader, payload: false }])
            )
        )
    );

    @Effect()
    getMgmCode$ = this.actions$.pipe(
        ofType(EEdpeActions.GetMgmCode),
        exhaustMap(() => {
            this.store.dispatch(new ShowLoader(true));
            return this.edpeService.getMgmCode().pipe(
                switchMap((mgm: MgmCode) => [
                    { type: ELayoutActions.ShowLoader, payload: false },
                    { type: EEdpeActions.SetMgmCode, payload: mgm },
                ]),
                catchError(() => [{ type: ELayoutActions.ShowLoader, payload: false }])
            );
        })
    );

    @Effect()
    validateMgmCode$ = this.actions$.pipe(
        ofType(EEdpeActions.ValidateMgmCode),
        switchMap((action: any) =>
            this.edpeService.validateMgmCode(action.payload).pipe(
                switchMap(() => [{ type: ELayoutActions.ShowLoader, payload: false }]),
                catchError(() => [{ type: ELayoutActions.ShowLoader, payload: false }])
            )
        )
    );

    @Effect()
    sendShareEmail$ = this.actions$.pipe(
        ofType(EEdpeActions.SendShareEmail),
        switchMap((action: any) =>
            this.edpeService.sendShareEmail(action.payload).pipe(
                switchMap(() => [{ type: ELayoutActions.LastResponse, response: true }]),
                catchError(() => [{ type: ELayoutActions.LastResponse, response: false }])
            )
        )
    );

    @Effect()
    sendShareSocial$ = this.actions$.pipe(
        ofType(EEdpeActions.SendShareSocial),
        switchMap((action: any) =>
            this.edpeService.sendShareSocial(action.payload).pipe(
                switchMap(() => [{ type: ELayoutActions.LastResponse, response: true }]),
                catchError(() => [{ type: ELayoutActions.LastResponse, response: false }])
            )
        )
    );

    @Effect()
    updateMgmAlert$ = this.actions$.pipe(
        ofType(EEdpeActions.UpdateMgmAlert),
        switchMap((action: any) =>
            this.edpeService.updateMgmAlert(action.payload).pipe(
                switchMap(() => [{ type: ELayoutActions.ShowLoader, payload: false }]),
                catchError(() => [{ type: ELayoutActions.ShowLoader, payload: false }])
            )
        )
    );

    @Effect()
    getQuizzs$ = this.actions$.pipe(
        ofType(EEdpeActions.GetQuizzs),
        switchMap(() => this.edpeService.getQuizzs()),
        mergeMap((response: any) => {
            const ongoing: Card[] = [];
            const finished: Card[] = [];

            response.finished.forEach(card => {
                const cardItem: Card = BenefitsFactory.create(card);
                finished.push(cardItem);
            });

            response.ongoing.forEach(card => {
                const cardItem: Card = BenefitsFactory.create(card);
                ongoing.push(cardItem);
            });

            return of(new SetQuizzs({ongoing, finished}), new SetLoadedDashboard(true));

        })
    );

    constructor(
        private readonly actions$: Actions,
        private readonly edpeService: EdpeService,
        private readonly edpModalService: EdpModalService,
        private readonly store: Store<IAppState>
    ) {}
}
